/**
 * FO-115 (batch-22) — portal-periode-keuze voor het eigen-uren-overzicht.
 *
 * Zet de zichtbare keuzes in de medewerker-app (deze week / deze maand / vorige
 * maand / verloningsperiode / eigen periode) om naar een concrete
 * `[van, totEnMet]`-range + label, bovenop het bestaande payroll-periodefilter
 * (`resolvePayrollPeriode`): één rekenkern, geen tweede. De NL-kalender
 * (jaar/maand/ISO-week) wordt uit het referentie-instant afgeleid in
 * Europe/Amsterdam (niet uit `process.tz`), zodat "deze maand"/"deze week" ook
 * rond middernacht NL en op DST-randen klopt.
 *
 * FO-491 (batch-66) — een maand is voortaan de KALENDERmaand (1e t/m laatste
 * dag). "Verloningsperiode" (spec 191 §2.3) toont bewust de andere variant: de
 * hele ISO-weken die de maand raken, met het weekbereik in het label. Elke
 * keuze draagt daarom de `modus` waarmee de API de periode moet optellen.
 *
 * Let op: de naam belooft meer dan de weergave doet. De verloning telt de
 * basisuren per kalenderdag van de maand (`allocateDaysToMonth`) en rekent
 * week-overuren toe aan de maand van de donderdag; ze telt geen hele weken. Deze
 * keuze is dus geen afspiegeling van de loonstrook. De spec ging daar wel van
 * uit; dat ligt bij de PO.
 *
 * Pure functie, zero deps (CLAUDE.md: geen date-fns/dayjs).
 */

import type { UrenPeriodeModus } from '@streep/types'
import { resolvePayrollPeriode, type PayrollPeriodeRange } from './payroll-periode'
import { utcToAmsterdamWallclock } from './amsterdam-tijd'
import { getIsoWeek } from './uren-calculations'
import { heleWekenBereik } from './uren-periode-knip'

export const PORTAL_UREN_PERIODE_KINDS = [
  'DEZE_WEEK',
  'DEZE_MAAND',
  'VORIGE_MAAND',
  'VERLONINGSPERIODE',
  'EIGEN',
] as const
export type PortalUrenPeriodeKind = (typeof PORTAL_UREN_PERIODE_KINDS)[number]

export const PORTAL_UREN_PERIODE_LABELS: Record<PortalUrenPeriodeKind, string> = {
  DEZE_WEEK:         'Deze week',
  DEZE_MAAND:        'Deze maand',
  VORIGE_MAAND:      'Vorige maand',
  VERLONINGSPERIODE: 'Verloningsperiode',
  EIGEN:             'Eigen periode',
}

export interface PortalUrenPeriodeInput {
  kind: PortalUrenPeriodeKind
  /** Referentie-instant; default `new Date()`. De NL-kalender wordt eruit afgeleid. */
  now?: Date
  /** Bij EIGEN: vrije grenzen (yyyy-mm-dd). */
  van?: string | null
  totEnMet?: string | null
  /**
   * Bij VERLONINGSPERIODE: de maand (`yyyy-mm`) waarvan de hele weken gevraagd
   * worden. Weggelaten = de lopende NL-maand.
   */
  verloningsMaand?: string | null
}

/** Een periode-keuze, klaar om naar de API te sturen. */
export interface PortalUrenPeriodeRange extends PayrollPeriodeRange {
  /** Hoe de API de periode optelt: geknipt op de periode, of over hele weken. */
  modus: UrenPeriodeModus
}

/** `dd-mm` (of `dd-mm-jjjj` met jaar) uit een `yyyy-mm-dd`. */
function kort(iso: string, metJaar: boolean): string {
  const dag = `${iso.slice(8, 10)}-${iso.slice(5, 7)}`
  return metJaar ? `${dag}-${iso.slice(0, 4)}` : dag
}

function parseMaand(waarde: string | null | undefined): { jaar: number; maand: number } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(waarde ?? '')
  if (!m) return null
  const maand = Number(m[2])
  if (maand < 1 || maand > 12) return null
  return { jaar: Number(m[1]), maand }
}

/**
 * Resolve een portal-periode-keuze naar een concrete `[van, totEnMet]`-range +
 * label + optel-modus. Gooit (net als `resolvePayrollPeriode`) bij een
 * onvolledige/ongeldige EIGEN-keuze (begin na eind, ontbrekende datum) of een
 * ongeldige verloningsmaand; de caller mapt dat naar een inline veldfout.
 */
export function resolvePortalUrenPeriode(input: PortalUrenPeriodeInput): PortalUrenPeriodeRange {
  const now = input.now ?? new Date()
  // NL-vandaag (Europe/Amsterdam), DST-veilig — niet uit process.tz.
  const { ymd } = utcToAmsterdamWallclock(now)
  const jaar = parseInt(ymd.slice(0, 4), 10)
  const maand = parseInt(ymd.slice(5, 7), 10)

  switch (input.kind) {
    case 'DEZE_WEEK': {
      // ISO-week van de NL-kalenderdag (de ISO-week is een pure kalender-notie).
      // Een hele week is in beide modi gelijk; WEKEN benoemt wat het is.
      const iso = getIsoWeek(new Date(`${ymd}T00:00:00.000Z`))
      return { ...resolvePayrollPeriode({ type: 'WEEK', jaar: iso.year, week: iso.week }), modus: 'WEKEN' }
    }
    case 'DEZE_MAAND':
      return { ...resolvePayrollPeriode({ type: 'MAAND', jaar, maand }), modus: 'KALENDER' }
    case 'VORIGE_MAAND': {
      const vmJaar = maand === 1 ? jaar - 1 : jaar
      const vmMaand = maand === 1 ? 12 : maand - 1
      return {
        ...resolvePayrollPeriode({ type: 'MAAND', jaar: vmJaar, maand: vmMaand }),
        modus: 'KALENDER',
      }
    }
    case 'VERLONINGSPERIODE': {
      const gekozen =
        input.verloningsMaand == null || input.verloningsMaand === ''
          ? { jaar, maand }
          : parseMaand(input.verloningsMaand)
      if (!gekozen) throw new Error('Kies een geldige maand.')
      const kalender = resolvePayrollPeriode({ type: 'MAAND', ...gekozen })
      const weken = heleWekenBereik(kalender.van, kalender.totEnMet)
      // Over de jaargrens (januari, december) tonen beide datums hun jaar.
      const metJaar = weken.van.slice(0, 4) !== weken.totEnMet.slice(0, 4)
      return {
        van: weken.van,
        totEnMet: weken.totEnMet,
        modus: 'WEKEN',
        label:
          `Verloningsperiode ${kalender.label}: week ${weken.eersteWeek.week} t/m ` +
          `${weken.laatsteWeek.week} (${kort(weken.van, metJaar)} t/m ${kort(weken.totEnMet, metJaar)})`,
      }
    }
    case 'EIGEN':
      return {
        ...resolvePayrollPeriode({
          type: 'VRIJ',
          jaar,
          van: input.van ?? null,
          totEnMet: input.totEnMet ?? null,
        }),
        modus: 'KALENDER',
      }
  }
}

export interface PortalVerloningsMaandOptie {
  /** `yyyy-mm`. */
  waarde: string
  /** "september 2026". */
  label: string
}

/**
 * De maanden waaruit de monteur bij "Verloningsperiode" kiest: de lopende
 * NL-maand en de maanden ervoor, nieuwste eerst. Een jaar terug is ruim genoeg
 * om terug te kijken; verder terug kan via "Eigen periode".
 */
export function portalVerloningsMaandOpties(
  now: Date = new Date(),
  aantal = 12,
): PortalVerloningsMaandOptie[] {
  const { ymd } = utcToAmsterdamWallclock(now)
  let jaar = parseInt(ymd.slice(0, 4), 10)
  let maand = parseInt(ymd.slice(5, 7), 10)
  const opties: PortalVerloningsMaandOptie[] = []
  for (let i = 0; i < aantal; i++) {
    opties.push({
      waarde: `${jaar}-${String(maand).padStart(2, '0')}`,
      label: resolvePayrollPeriode({ type: 'MAAND', jaar, maand }).label,
    })
    if (maand === 1) {
      maand = 12
      jaar -= 1
    } else {
      maand -= 1
    }
  }
  return opties
}
